import React, { useState } from 'react';
import { CheckCircle, Clock, X } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

interface Circular {
  id:         string;
  titleMs:    string;
  titleEn:    string;
  issuedDate: string;
  bodyMs:     string;
  bodyEn:     string;
  acknowledged: boolean;
  ackedAt?:   string;
}

const CIRCULARS: Circular[] = [
  {
    id: 'c1',
    titleMs: 'Pekeliling: Peperiksaan Pertengahan Tahun 2025',
    titleEn: 'Circular: Mid-Year Examination 2025',
    issuedDate: '15 Mei 2025',
    bodyMs: `Dengan hormatnya dimaklumkan bahawa Peperiksaan Pertengahan Tahun 2025 akan diadakan pada 9–13 Jun 2025.

Ibu bapa / penjaga diminta memastikan:
1. Pelajar hadir tepat pada masanya setiap hari peperiksaan.
2. Pelajar membawa alatan tulis yang mencukupi.
3. Telefon bimbit TIDAK dibenarkan dibawa masuk ke dewan peperiksaan.

Jadual peperiksaan terperinci akan diedarkan kepada pelajar secara berasingan.

Kerjasama dan sokongan ibu bapa / penjaga amat dihargai.`,
    bodyEn: `We wish to inform that the Mid-Year Examination 2025 will be held on 9–13 June 2025.

Parents / guardians are requested to ensure:
1. Students arrive punctually on each examination day.
2. Students bring sufficient stationery.
3. Mobile phones are NOT permitted in the examination hall.

A detailed examination timetable will be distributed to students separately.

Your cooperation and support are greatly appreciated.`,
    acknowledged: false,
  },
  {
    id: 'c2',
    titleMs: 'Pekeliling: Aktiviti Ko-Kurikulum & Sukan Tahunan',
    titleEn: 'Circular: Co-curricular Activities & Annual Sports Day',
    issuedDate: '3 Jun 2025',
    bodyMs: `Sukacita dimaklumkan bahawa Sukan Tahunan Sekolah 2025 akan diadakan pada 20 Jun 2025 di padang sekolah.

Semua pelajar dikehendaki hadir. Ibu bapa dijemput hadir untuk memberi sokongan kepada anak masing-masing.

Pelajar perlu memakai pakaian sukan sekolah pada hari tersebut.`,
    bodyEn: `We are pleased to inform you that the Annual Sports Day 2025 will be held on 20 June 2025 at the school field.

All students are required to attend. Parents are invited to show their support.

Students must wear the school sports attire on that day.`,
    acknowledged: true,
    ackedAt: '4 Jun 2025, 10:32 pagi',
  },
  {
    id: 'c3',
    titleMs: 'Borang Kebenaran: Lawatan Sambil Belajar ke Muzium Brunei',
    titleEn: 'Consent Form: Educational Visit to Brunei Museum',
    issuedDate: '28 Mei 2025',
    bodyMs: `Pihak sekolah merancang untuk mengadakan lawatan sambil belajar ke Muzium Brunei pada 18 Jun 2025.

Yuran pengangkutan: BND 5.00 per pelajar.

Ibu bapa / penjaga MESTI menandatangani borang kebenaran ini bagi membolehkan anak anda menyertai lawatan tersebut. Pelajar yang tidak mempunyai kebenaran tidak akan dibenarkan menyertai lawatan.`,
    bodyEn: `The school is planning an educational visit to the Brunei Museum on 18 June 2025.

Transport fee: BND 5.00 per student.

Parents / guardians MUST sign this consent form for your child to participate. Students without consent will not be permitted to join.`,
    acknowledged: false,
  },
];

export function ParentPekelilingPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'Circulars' : 'Pekeliling');

  const [circulars, setCirculars] = useState<Circular[]>(CIRCULARS);
  const [openModal, setOpenModal] = useState<Circular | null>(null);
  const [checked, setChecked]     = useState(false);
  const [toastMsg, setToastMsg]   = useState<string | null>(null);

  function acknowledge(id: string) {
    const now = new Date();
    const ts  = `${now.getDate()} ${['Jan','Feb','Mac','Apr','Mei','Jun','Jul','Ogos','Sep','Okt','Nov','Dis'][now.getMonth()]} ${now.getFullYear()}, ${now.toLocaleTimeString('ms-MY', { hour:'2-digit', minute:'2-digit' })}`;
    setCirculars(cs => cs.map(c => c.id === id ? { ...c, acknowledged: true, ackedAt: ts } : c));
    setOpenModal(null);
    setChecked(false);
    setToastMsg(E ? `Acknowledgement recorded at ${ts}` : `Pengesahan berjaya direkodkan pada ${ts}`);
    setTimeout(() => setToastMsg(null), 4000);
  }

  const pending = circulars.filter(c => !c.acknowledged).length;

  return (
    <div style={{ maxWidth: '800px' }}>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
            {E ? 'Circulars & Consent' : 'Pekeliling & Kebenaran'}
          </h1>
          <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
            {pending > 0
              ? (E ? `${pending} circular(s) awaiting your acknowledgement` : `${pending} pekeliling menunggu pengesahan anda`)
              : (E ? 'All circulars acknowledged' : 'Semua pekeliling telah disahkan')}
          </p>
        </div>
      </div>

      {/* Toast */}
      {toastMsg && (
        <div style={{ background: T.color.successLight, border: `1px solid ${T.color.success}40`, borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CheckCircle size={16} color={T.color.success} />
          <span style={{ fontSize: '13px', color: T.color.success }}>{toastMsg}</span>
        </div>
      )}

      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {circulars.map(c => (
          <div key={c.id} style={{
            background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '20px 24px',
            display: 'flex', alignItems: 'flex-start', gap: '16px',
          }}>
            {/* Status icon */}
            <div style={{ flexShrink: 0, marginTop: '2px' }}>
              {c.acknowledged
                ? <CheckCircle size={22} color={T.color.success} />
                : <Clock size={22} color={T.color.warning} />}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: T.color.textPrimary, marginBottom: '4px' }}>
                {E ? c.titleEn : c.titleMs}
              </div>
              <div style={{ fontSize: '12px', color: T.color.textSecondary, marginBottom: '10px' }}>
                {E ? 'Issued:' : 'Dikeluarkan:'} {c.issuedDate}
              </div>
              {c.acknowledged ? (
                <span className="badge" style={{ background: T.color.successLight, color: T.color.success }}>
                  ✓ {E ? 'Acknowledged' : 'Disahkan'} · {c.ackedAt}
                </span>
              ) : (
                <span className="badge" style={{ background: T.color.warningLight, color: T.color.warning }}>
                  {E ? 'Pending Acknowledgement' : 'Belum Disahkan'}
                </span>
              )}
            </div>

            {/* Action */}
            <div style={{ flexShrink: 0 }}>
              {c.acknowledged ? (
                <button className="btn-ghost btn-sm" style={{ fontSize: '12px' }} onClick={() => { setOpenModal(c); setChecked(true); }}>
                  {E ? 'View' : 'Lihat'}
                </button>
              ) : (
                <button className="btn-primary btn-sm" onClick={() => { setOpenModal(c); setChecked(false); }}>
                  {E ? 'Read & Acknowledge' : 'Baca & Sahkan'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {openModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#FFFFFF', borderRadius: '12px', width: '100%', maxWidth: '560px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            {/* Modal header */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: T.color.textPrimary, lineHeight: 1.3 }}>
                  {E ? openModal.titleEn : openModal.titleMs}
                </div>
                <div style={{ fontSize: '12px', color: T.color.textSecondary, marginTop: '4px' }}>
                  {E ? 'Issued:' : 'Dikeluarkan:'} {openModal.issuedDate}
                </div>
              </div>
              <button className="btn-icon" onClick={() => { setOpenModal(null); setChecked(false); }}>
                <X size={18} />
              </button>
            </div>

            {/* Modal body — scrollable circular text */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              <p style={{ fontSize: '13px', color: T.color.textPrimary, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                {E ? openModal.bodyEn : openModal.bodyMs}
              </p>
            </div>

            {/* Modal footer */}
            {!openModal.acknowledged && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => setChecked(e.target.checked)}
                    style={{ marginTop: '2px', cursor: 'pointer', accentColor: '#F7D000' }}
                  />
                  <span style={{ fontSize: '13px', color: T.color.textPrimary }}>
                    {E
                      ? 'I have read and understood this circular.'
                      : 'Saya telah membaca dan memahami pekeliling ini.'}
                  </span>
                </label>
                <button
                  className="btn-primary"
                  disabled={!checked}
                  style={{ width: '100%', height: '44px', opacity: checked ? 1 : 0.4 }}
                  onClick={() => acknowledge(openModal.id)}
                >
                  {E ? 'Confirm Acknowledgement' : 'Sahkan Penerimaan'}
                </button>
              </div>
            )}
            {openModal.acknowledged && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <CheckCircle size={16} color={T.color.success} />
                  <span style={{ fontSize: '13px', color: T.color.success, fontWeight: 600 }}>
                    {E ? `Acknowledged · ${openModal.ackedAt}` : `Disahkan · ${openModal.ackedAt}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
